import { useState, useRef, useEffect } from "react";
import { categories } from "@/mocks/categories";

export default function CategorySection() {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleSelect = (cat: typeof categories[0]) => {
    setSelected(cat.name);
    setOpen(false);
    window.open(cat.url, "_blank", "noopener,noreferrer");
  };

  return (
    <section className="relative w-full bg-background-50 py-16 md:py-20">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-3">
            Przeglądaj kategorie produktów
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
            Wybierz kategorię, aby przejść do pełnej oferty na Kaspreadsheet
          </p>
        </div>

        {/* Dropdown - mobile optimized */}
        <div className="max-w-md mx-auto mb-12" ref={dropdownRef}>
          <div className="relative">
            <button
              onClick={() => setOpen(!open)}
              className="w-full flex items-center justify-between px-5 py-3.5 bg-background-100 border border-background-200 rounded-xl text-sm text-foreground-800 hover:border-accent-300 transition-all duration-200 cursor-pointer"
            >
              <span className={selected ? "text-foreground-800" : "text-foreground-400"}>
                {selected || "Wybierz kategorię..."}
              </span>
              <i className={`ri-arrow-down-s-line text-lg text-foreground-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`}></i>
            </button>

            {open && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-background-50 border border-background-200 rounded-xl shadow-xl z-20 max-h-72 overflow-y-auto">
                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => handleSelect(cat)}
                    className="w-full flex items-center gap-4 px-5 py-3 text-sm text-foreground-700 hover:bg-accent-50 hover:text-accent-800 transition-colors duration-150 text-left cursor-pointer first:rounded-t-xl last:rounded-b-xl"
                  >
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                      <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <span className="font-medium">{cat.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Category cards grid with images */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
          {categories.map((cat) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group flex flex-col rounded-xl bg-background-50 border border-background-200 hover:border-primary-200 overflow-hidden transition-all duration-200 cursor-pointer"
            >
              {/* Category image */}
              <div className="relative w-full aspect-[4/3] overflow-hidden bg-background-100">
                <img
                  src={cat.image}
                  alt={cat.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
              </div>
              {/* Category name */}
              <div className="flex items-center gap-2 px-3 py-3">
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-accent-100 group-hover:bg-accent-200 transition-colors duration-200 flex-shrink-0">
                  <i className={`${cat.icon} text-xs text-accent-600`}></i>
                </div>
                <span className="text-xs md:text-sm font-medium text-foreground-700 group-hover:text-foreground-900 transition-colors duration-200 whitespace-nowrap truncate">
                  {cat.name}
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}